import { ActionType } from '../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, makeApiReducer, ApiReducer } from '../../core/api/rsaa';
import { Endpoints } from '../../core/api/endpoints';
import { getLoading, addLoading, addError, noLoadingNoError } from '../../core/api/module';
import * as R from 'ramda';


const KEY_ACTION = ActionType.SetGoalAnswerRequest;
const ACTIONS    = [
  ActionType.SetGoalAnswerRequest,
  ActionType.SetGoalAnswerResponse,
  ActionType.SetGoalAnswerError,
];


type Payload = string;


type Meta = {
  projectId: number;
  goalQuestionId: number;
  answer: string;
};


/*
 * Dispatch this action to set an answer for one of the project goals
*/
export const setGoalAnswerAction = (
  projectId: number,
  participantId: number,
  goalQuestionId: number,
  answer: string,
) => makeApiAction<Payload, Meta>({
  method:   'POST',
  endpoint: Endpoints.SetGoalAnswer,
  headers:  {
    'content-type': 'application/json',
    'accept':       'application/json',
  },
  body: JSON.stringify({
    projectId: projectId,
    participantId: participantId,
    questionId: goalQuestionId,
    questionAnswer: answer,
  }),
  types:    makeRsaaTypes<Payload, Meta>(
    ACTIONS,
    {
      projectId:      projectId,
      goalQuestionId: goalQuestionId,
      answer:         answer,
    },
  ),  
  bailout:  state => !!getLoading(state, KEY_ACTION),
});


const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.projectId}-${action.meta.goalQuestionId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  // The set goal answer endpoint could retrun 200 on failure, but with an informative error message
  if (payload !== 'Success') {
    return addError(state, KEY_ACTION, payload || '');
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  const goalsAnswers   = state.goalsAnswers?.[action.meta.projectId] || {};
  const editing = R.filter(
    key => goalsAnswers[key].questionId === action.meta.goalQuestionId,
    Object.keys(goalsAnswers),
  );
  const changed = R.pick(editing, goalsAnswers);
  const updated = Object.values(changed).reduce(
    (map, goal) => R.assoc(
      `${goal.questionId}`,
      R.mergeRight(goal, { questionAnswer: `${action.meta.answer}` }),
      map,
    ),
    {},
  );
  const updatedGoalsAnswers = R.mergeRight(goalsAnswers, updated);

  return R.assocPath(
    ['goalsAnswers', `${action.meta.projectId}`],
    updatedGoalsAnswers,
    successState,
  );


};


export const setGoalAnswerReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
